import { LightMode, RiksTVProvider } from '@rikstv/shared-components';
import { captureMessage } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { MaintenanceMode } from './MaintenanceMode';
import { Chatbot } from './chatbot/Chatbot';
import { FeatureFlagControlPanel } from './featureflags/feature-flag-panel';
import Header from './header/Header';
import { Modal } from './modal/Modal';
import OnRouteChange from './on-route-change/OnRouteChange';
import { CustomerVerificationCallback } from './routes/callback/CustomerVerificationCallback';
import NotFound from './routes/not-found/NotFound';
import Receipt from './routes/receipt/Receipt';
import { CampaignV2 } from './routes/v2/CampaignV2';
import { SelectSubscription } from './routes/v2/SelectSubscription';
import { useFlowSteps, v2Routes } from './routes/v2/v2routes';
import { Loader } from './shared';
import { InternetLandingPage, Route_InternetLandingPage } from './routes/internet/InternetLandingPage';
import { InternetOfferSubPage, Route_InternetOfferSubPage } from './routes/internet/InternetOfferSubPage';
import { useFeatureFLags } from '~/hooks/useFeatureFlags';

const App: React.FC = () => {
  const { redirecting } = useRedirectService();
  const { rtvWebInternetSales } = useFeatureFLags();
  const v2Steps = useFlowSteps();

  return (
    <RiksTVProvider>
      <LightMode>
        <div id="app">
          <MaintenanceMode>
            <OnRouteChange>
              <Modal>
                <Header />
                <Routes>
                  <Route path="/callback" element={<CustomerVerificationCallback />} />
                  <Route path="/bekreftelse" element={<Receipt />} />
                  {/* Internet package deals flow */}
                  {rtvWebInternetSales && (
                    <Route path={Route_InternetLandingPage}>
                      <Route index element={<InternetLandingPage />} />
                      <Route path={Route_InternetOfferSubPage} element={<InternetOfferSubPage />} />
                    </Route>
                  )}
                  {/* Regular sales flow*/}
                  <Route path={v2Routes.index}>
                    <Route index element={<SelectSubscription />} />
                    <Route path={v2Routes.campaign} element={<CampaignV2 />}>
                      {v2Steps.map((step) => (
                        <Route key={`v2-step-${step.stepPath}`} path={step.stepPath} element={step.element} />
                      ))}
                    </Route>
                  </Route>
                  {redirecting && <Route path="*" element={<Loader />} />}
                  {!redirecting && <Route path="*" element={<NotFound />} />}
                </Routes>
              </Modal>
            </OnRouteChange>
          </MaintenanceMode>
          <FeatureFlagControlPanel />
          <Chatbot />
        </div>
      </LightMode>
    </RiksTVProvider>
  );
};

const useRedirectService = () => {
  const navigate = useNavigate();
  const to = getPotentialRedirectUrl();
  const [redirecting, setRedirecting] = useState(to != null);

  useEffect(() => {
    if (to) {
      captureMessage('Redirect from old or deprecated campaign url', {
        level: 'info',
        fingerprint: ['redirect'],
        tags: { redirectTo: to, redirectFrom: globalThis.location.pathname },
      });
      navigate(to, { replace: true })?.then(() => {
        setRedirecting(false);
      });
    }
  }, [to, navigate]);

  return { redirecting };
};

const getPotentialRedirectUrl = () => {
  const pathSegments = globalThis.location.pathname.split('/').filter(Boolean);
  return getModernFlowRedirectUrl(pathSegments);
};

const validCampaignUrls = ['rikspakken', 'sportspakken', 'fleksipakken', 'strommepakken'];
// const validPartnerAliases = ['kampanje', 'homenet', 'gp', 'nextgentel'];
const validCustomerTypes = ['bolig', 'borettslag'];

const getModernFlowRedirectUrl = (pathSegments: string[]): string | undefined => {
  if (pathSegments.length === 0) {
    // we're on "/"
    return undefined;
  }
  const [_segmentOne, segmentTwo, segmentThree] = pathSegments;
  if (_segmentOne === v2Routes.index.replace('/', '')) {
    return undefined;
  }
  const validCampaignInSegmentTwo = validCampaignUrls.includes(segmentTwo);

  if (validCampaignInSegmentTwo) {
    return `${v2Routes.index}${segmentTwo}`;
  }
  const validCampaignInSegmentThree = validCampaignUrls.includes(segmentThree);
  if (validCampaignInSegmentThree) {
    return `${v2Routes.index}${segmentThree}`;
  }
  const validCustomerTypeInSegmentTwo = validCustomerTypes.includes(segmentTwo);
  if (validCustomerTypeInSegmentTwo) {
    return `${v2Routes.index}`;
  }
};

export default App;
